import { useLocation, useNavigationType } from 'react-router';
import { historyIndex } from './navTransition';

/**
 * WHERE THE PERSON STANDS IN THEIR OWN STACK (wave 4 round 1, C2-7; owner-76: "Hier gibt es
 * auch keinen Zurueck-Button ... ich denk schon. Vor allem bei solchen Apps").
 *
 * Finder, App Store and System Settings all carry a back and forward pair at the leading
 * edge of the window, and Apple states both the placement - "Elements that let people
 * return to the previous document and show or hide a sidebar appear at the far leading
 * edge" - and the disabling rule - "The system disables backward navigation controls when
 * the stack is empty and the root view ... is visible" (hig:
 * https://developer.apple.com/design/human-interface-guidelines/toolbars;
 * https://developer.apple.com/documentation/swiftui/navigationstack).
 *
 * A BROWSER WILL NOT SAY WHETHER THERE IS ANYTHING AHEAD, so the pair cannot ask it. What it
 * can read is where it stands: `createBrowserHistory` writes `{usr, key, idx}` into
 * `history.state` and `idx` is the position in this document's stack, which `useBack`
 * already relies on. Back is available at any index above zero. Forward is available while
 * the index is below the deepest one the stack still holds - and only the ROUTER can say
 * what that is, because a push TRUNCATES everything ahead of it and a pop does not.
 * `useNavigationType` is the router's own word for which of the two just happened, so the
 * mark follows a push down as well as up and never offers a forward step into an entry the
 * browser has already thrown away.
 *
 * The mark is per document and resets with a reload, the way Safari's forward arrow does
 * after a cold load. It is a module-level number rather than state because it is a property
 * of the WINDOW: two toolbars rendered at once (a states-page specimen beside the real
 * shell) have to answer the same thing.
 */
let deepest = 0;

/** The index this document is standing at, or 0 when nothing has written one yet. */
function currentIndex(): number {
  if (typeof window === 'undefined') return 0;
  return historyIndex(window.history.state) ?? 0;
}

export interface HistoryPosition {
  readonly canGoBack: boolean;
  readonly canGoForward: boolean;
}

export function useHistoryPosition(): HistoryPosition {
  /* Subscribing to the location is what re-runs this hook on every navigation; the value
     itself is not needed, because the router has already written the new index into
     `history.state` by the time this render happens. */
  useLocation();
  const navigationType = useNavigationType();
  const at = currentIndex();
  /* READ AND WRITTEN DURING THE RENDER, for the reason `AppShell` gives for the tab's own
     memory: the answer has to be right in the frame the toolbar paints, and an effect has
     not run yet when a person navigates twice in a row. Both branches are idempotent, so a
     double render under StrictMode changes nothing. */
  if (navigationType === 'PUSH') deepest = at;
  else deepest = Math.max(deepest, at);
  return { canGoBack: at > 0, canGoForward: at < deepest };
}
